/* HOQ: quantity-backed, same-variant collection stock filters. */
(() => {
  'use strict';
  const facets = {size: 'Ring size', gold: 'Gold type', purity: 'Metal purity', colour: 'Metal colour'};
  const normal = value => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  function facet(name) {
    const n = normal(name);
    if (n.includes('ringsize')) return 'size';
    if (n.includes('purity') || n === 'karat' || n === 'carat' || n === 'metal') return 'purity';
    if (n.includes('color') || n.includes('colour')) return 'colour';
    if (n.includes('goldtype') || n.includes('metaltype')) return 'gold';
    return null;
  }
  function caratNumber(value) {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const match = String(value).trim().match(/^(\d+(?:\.\d+)?)\s*(?:ct|cts|carat|carats)?$/i);
    return match ? Number(match[1]) : null;
  }
  function caratBucket(value) {
    const n = caratNumber(value);
    return n !== null && n >= 1 && n <= 5 ? String(Math.floor(n)) : null;
  }
  function matches(variant, selection, ignored) {
    return Object.keys(facets).every(key => key === ignored || !selection[key].length || selection[key].includes(variant.facets[key]));
  }
  function nativeMatch(variant, product, params) {
    const availability = params.getAll('filter.v.availability');
    if (availability.length && !availability.includes(variant.available ? '1' : '0')) return false;
    const groups = new Map();
    for (const [key, value] of params) {
      if (!key.startsWith('filter.v.option.')) continue;
      const index = product.names.findIndex(name => normal(name) === normal(key.slice(16)));
      if (index < 0) return false;
      if (!groups.has(index)) groups.set(index, []);
      groups.get(index).push(value);
    }
    for (const [index, values] of groups) if (!values.includes(variant.options[index])) return false;
    const min = params.get('filter.v.price.gte');
    const max = params.get('filter.v.price.lte');
    return (!min || variant.price >= Number(min) * 100) && (!max || variant.price <= Number(max) * 100);
  }
  // Kept separate for deterministic tests of same-variant matching.
  if (typeof module !== 'undefined' && module.exports) { module.exports = {facet, matches, nativeMatch, caratNumber, caratBucket}; return; }
  class StockFilters {
    constructor(root) {
      this.root = root;
      this.sidebar = root.querySelector('[data-collection-sidebar]');
      this.original = root.querySelector('[data-products-grid]');
      if (!this.sidebar || !this.original) return;
      this.pageSize = JSON.parse(root.querySelector('[data-hoq-stock-config]').textContent).pageSize || 24;
      this.limit = this.pageSize;
      this.output = document.createElement('div');
      this.output.className = 'grid-outer hoq-stock-results';
      this.output.hidden = true;
      this.original.after(this.output);
      this.sidebar.addEventListener('input', event => {
        if (event.target.closest('[data-hoq-stock-panel], [data-hoq-carat-panel]')) event.stopImmediatePropagation();
      }, true);
      this.sidebar.addEventListener('change', event => {
        if (!event.target.closest('[data-hoq-stock-panel], [data-hoq-carat-panel]')) return;
        event.stopImmediatePropagation();
        const url = new URL(location.href);
        if (event.target.dataset.stock === 'toggle') {
          url.searchParams.delete('hoq_stock');
          if (event.target.checked) url.searchParams.set('hoq_stock', '1');
          else Object.keys(facets).forEach(key => url.searchParams.delete('hoq_' + key));
        } else if (event.target.dataset.carat) {
          url.searchParams.delete('hoq_carat');
          if (event.target.checked) url.searchParams.set('hoq_carat', event.target.value);
        } else {
          const key = event.target.dataset.facet;
          if (!key) return;
          const values = url.searchParams.getAll('hoq_' + key).filter(value => value !== event.target.value);
          if (event.target.checked) values.push(event.target.value);
          url.searchParams.delete('hoq_' + key);
          values.forEach(value => url.searchParams.append('hoq_' + key, value));
          url.searchParams.set('hoq_stock', '1');
        }
        url.searchParams.delete('page');
        history.pushState(null, '', url);
        this.limit = this.pageSize;
        this.render();
      });
      this.sidebar.addEventListener('click', event => {
        const button = event.target.closest('[data-stock-clear], [data-stock-retry], [data-carat-clear]');
        if (!button) return;
        event.preventDefault();
        if (button.hasAttribute('data-stock-retry')) { this.key = null; this.refresh(); return; }
        const url = new URL(location.href);
        if (button.hasAttribute('data-carat-clear')) url.searchParams.delete('hoq_carat');
        else ['stock', ...Object.keys(facets)].forEach(key => url.searchParams.delete('hoq_' + key));
        url.searchParams.delete('page');
        history.pushState(null, '', url);
        this.render();
      });
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this.sidebar, {childList: true});
      window.addEventListener('popstate', () => {this.limit = this.pageSize; this.refresh();});
      this.refresh();
    }
    params() {
      const params = new URLSearchParams(location.search);
      ['page', 'section_id', 'view', 'hoq_stock', 'hoq_carat', ...Object.keys(facets).map(k => 'hoq_' + k)].forEach(k => params.delete(k));
      return params;
    }
    async refresh() {
      const params = this.params();
      const key = params.toString();
      if (key === this.key) {this.render(); return;}
      this.key = key;
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.ready = false; this.error = ''; this.limit = this.pageSize;
      this.render();
      try {
        const products = [];
        const visited = new Set();
        let page = 1;
        do {
          const url = new URL(location.pathname, location.origin);
          url.search = params.toString();
          url.searchParams.set('view', 'hoq-stock-data');
          url.searchParams.set('page', page);
          const response = await fetch(url, {signal: controller.signal, credentials: 'same-origin'});
          if (!response.ok) throw new Error('Collection lookup failed. Please retry.');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const feed = doc.querySelector('[data-hoq-stock-feed]');
          if (!feed) throw new Error('Collection filter files are missing. Please contact us.');
          if (Number(feed.dataset.total) > 25000 || feed.querySelector('[data-hoq-stock-overflow]')) throw new Error('Custom filtering cannot load this collection completely (over 25,000 products or 250 variants per product). Please contact us.');
          for (const node of feed.querySelectorAll('[data-hoq-stock-product]')) {
            const product = JSON.parse(node.querySelector('script').textContent);
            if (visited.has(product.id)) continue;
            visited.add(product.id);
            product.html = node.querySelector('template').innerHTML;
            product.variants = product.variants.map(variant => {
              variant.facets = {};
              product.names.forEach((name, i) => { const key = facet(name); if (key) variant.facets[key] = variant.options[i]; });
              return variant;
            }).filter(variant => nativeMatch(variant, product, params));
            if (product.variants.length) products.push(product);
          }
          if (!feed.dataset.next) break;
          const next = Number(new URL(feed.dataset.next, location.origin).searchParams.get('page'));
          if (!next || next <= page) throw new Error('Stock lookup could not finish. Please retry.');
          page = next;
        } while (true);
        if (controller.signal.aborted) return;
        this.products = products; this.ready = true;
      } catch (error) {
        if (controller.signal.aborted) return;
        this.error = error.message || 'Collection lookup failed. Please retry.';
      }
      this.render();
    }
    render() {
      const panel = this.sidebar.querySelector('[data-hoq-stock-panel]');
      if (!panel) return;
      const params = new URLSearchParams(location.search);
      const active = params.get('hoq_stock') === '1';
      const carat = ['1','2','3','4','5'].includes(params.get('hoq_carat')) ? params.get('hoq_carat') : '';
      const customActive = active || Boolean(carat);
      const selection = Object.fromEntries(Object.keys(facets).map(key => [key, params.getAll('hoq_' + key)]));
      const focus = document.activeElement;
      const focusedFacet = panel.contains(focus) ? focus.dataset.facet : null;
      const focusedValue = focus?.value;
      const focusedCarat = this.sidebar.contains(focus) && focus.dataset.carat;
      const focusedToggle = panel.contains(focus) && focus.dataset.stock === 'toggle';
      const closed = new Set([...panel.querySelectorAll('details:not([open])')].map(e => e.dataset.facetGroup));
      const previousCaratPanel = this.sidebar.querySelector('[data-hoq-carat-panel]');
      const caratOpen = previousCaratPanel?.querySelector('details')?.open ?? true;
      previousCaratPanel?.remove();
      panel.replaceChildren();
      // Keep the control visible on every collection, including /collections/all.
      panel.hidden = false;
      this.root.classList.toggle('hoq-stock-mode', customActive);
      this.output.hidden = !customActive;
      this.output.replaceChildren();
      const text = (tag, value, parent, className) => {
        const node = document.createElement(tag); node.textContent = value;
        if (className) node.className = className;
        parent.append(node); return node;
      };
      const check = (parent, labelText, checked) => {
        const label = document.createElement('label'); label.className = 'hoq-stock-control';
        const input = document.createElement('input'); input.type = 'checkbox'; input.checked = checked;
        label.append(input); text('span', labelText, label); parent.append(label); return input;
      };
      const toggle = check(panel, 'In-stock only' + (this.ready ? ` (${this.products.reduce((total, p) => total + p.variants.filter(v => v.stocked).length, 0)})` : ''), active);
      toggle.dataset.stock = 'toggle';
      const eligible = v => (!active || (v.stocked && matches(v, selection)));
      const caratPanel = document.createElement('div'); caratPanel.dataset.hoqCaratPanel = ''; caratPanel.className = 'filter-group hoq-carat-group';
      const caratDetails = document.createElement('details'); caratDetails.open = caratOpen; caratDetails.dataset.facetGroup = 'carat';
      caratDetails.className = 'hoq-carat-options';
      const caratHeading = text('summary', '', caratDetails, 'filter-group__heading');
      text('span', 'Centre Diamond Carat', caratHeading);
      caratHeading.insertAdjacentHTML('beforeend', '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-nav-arrow-down" viewBox="0 0 24 24"><path d="m6 9 6 6 6-6" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>');
      const caratFields = document.createElement('fieldset'); caratFields.setAttribute('aria-label', 'Centre Diamond Carat'); caratDetails.append(caratFields);
      for (const value of ['1','2','3','4','5']) {
        const count = this.ready ? this.products.filter(p => p.variants.some(v => eligible(v) && caratBucket(v.carat) === value)).length : null;
        const input = check(caratFields, `${value} CT${count === null ? '' : ` (${count})`}`, carat === value);
        input.dataset.carat = 'true'; input.value = value;
      }
      caratPanel.append(caratDetails);
      const priceGroup = this.sidebar.querySelector('[data-field-price-min]')?.closest('.filter-group');
      if (priceGroup) priceGroup.after(caratPanel);
      else panel.append(caratPanel);
      if (carat) {
        const b = text('button', 'Clear carat filter', caratPanel, 'hoq-stock-clear'); b.type = 'button'; b.dataset.caratClear = '';
        text('p', carat === '5' ? '5.00 CT centre diamonds' : `${carat} CT to below ${Number(carat) + 1} CT`, caratPanel, 'hoq-stock-status');
      }
      const clear = () => {const b = text('button', 'Clear stock filters', panel, 'hoq-stock-clear'); b.type = 'button'; b.dataset.stockClear = '';};
      if (!this.ready) {
        const message = this.error || 'Loading filters across this collection…';
        const status = text('p', message, panel, 'hoq-stock-status');
        status.setAttribute('role', 'status');
        if (customActive) {
          text('p', message, this.output, 'hoq-stock-status');
          clear();
        }
        if (this.error) {
          const b = text('button', 'Retry stock check', panel, 'hoq-stock-clear');
          b.type = 'button'; b.dataset.stockRetry = '';
        }
        if (focusedToggle) toggle.focus();
        return;
      }
      if (!customActive) { if (focusedToggle) toggle.focus(); return; }
      const options = document.createElement('div'); options.className = 'hoq-stock-options'; panel.append(options);
      if (active) for (const [key, title] of Object.entries(facets)) {
        const counts = new Map();
        for (const product of this.products) {
          const values = new Set(product.variants.filter(v => v.stocked && (!carat || caratBucket(v.carat) === carat) && matches(v, selection, key)).map(v => v.facets[key]).filter(Boolean));
          values.forEach(value => counts.set(value, (counts.get(value) || 0) + 1));
        }
        selection[key].forEach(value => { if (!counts.has(value)) counts.set(value, 0); });
        if (!counts.size) continue;
        const details = document.createElement('details'); details.open = !closed.has(key); details.dataset.facetGroup = key;
        text('summary', title, details);
        const fieldset = document.createElement('fieldset'); fieldset.setAttribute('aria-label', title); details.append(fieldset);
        [...counts].sort((a,b) => a[0].localeCompare(b[0], undefined, {numeric:true})).forEach(([value, count]) => {
          const input = check(fieldset, `${value} (${count})`, selection[key].includes(value));
          input.dataset.facet = key; input.value = value;
        });
        options.append(details);
      }
      if (active) clear();
      const results = this.products.map(product => {
        const variants = product.variants.filter(v => eligible(v) && (!carat || caratBucket(v.carat) === carat));
        if (carat) variants.sort((a, b) => caratNumber(a.carat) - caratNumber(b.carat));
        return {product, variant:variants[0]};
      }).filter(item => item.variant);
      if (carat) results.sort((a,b) => caratNumber(a.variant.carat) - caratNumber(b.variant.carat));
      const status = text('p', `${results.length}${active ? ' in-stock' : ''} product${results.length === 1 ? '' : 's'}${carat ? ' · Centre carat: low to high' : ''}`, this.output, 'hoq-stock-status');
      status.setAttribute('role', 'status');
      if (!results.length) text('p', 'No variants match these options. Clear a filter to see more products.', this.output);
      const grid = document.createElement('div'); grid.className = 'grid'; this.output.append(grid);
      results.slice(0, this.limit).forEach(({product, variant}) => {
        const template = document.createElement('template'); template.innerHTML = product.html;
        const fragment = template.content;
        fragment.querySelectorAll('[id]').forEach(el => {el.id = 'hoq-stock-' + el.id;});
        fragment.querySelectorAll('a[data-product-link]').forEach(link => {
          const url = new URL(link.getAttribute('href'), location.origin); url.searchParams.set('variant', variant.id);
          link.href = url.pathname + url.search;
          link.dataset.productLink = url.pathname + url.search;
        });
        grid.append(fragment);
      });
      if (results.length > this.limit) {
        const more = text('button', 'Load more products', this.output, 'btn btn--primary btn--outline hoq-stock-more');
        more.type = 'button'; more.onclick = () => {this.limit += this.pageSize; this.render(); this.output.querySelector('.hoq-stock-more')?.focus();};
      }
      if (focusedToggle) toggle.focus();
      else if (focusedCarat) [...caratPanel.querySelectorAll('[data-carat]')].find(input => input.value === focusedValue)?.focus();
      else if (focusedFacet) [...panel.querySelectorAll('input')].find(input => input.dataset.facet === focusedFacet && input.value === focusedValue)?.focus();
    }
  }
  function init() {
    document.querySelectorAll('collection-component').forEach(root => {
      if (!root.querySelector('[data-hoq-stock-config]') || root.hoqStockFilters) return;
      root.hoqStockFilters = new StockFilters(root);
    });
  }
  init();
  document.addEventListener('shopify:section:load', init);
})();
